import { Table, type Column, type Row } from '@ardoq/table';
import styled from 'styled-components';
import { useMemo } from 'react';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { TertiaryButton } from '@ardoq/button';
import { openReportInBuilder } from '../../components/AppMainSidebar/utils';
import { ReportDatasourceMigrationData } from '@ardoq/api-types';
import { Header1, Paragraph } from '@ardoq/typography';
import { ReportEventLocations } from '@ardoq/report-reader';
import { t, type I18nLocale } from '@ardoq/i18n';
import { getTableEmptyStateLabels } from 'locales/getTableLabels';
import { LoadingOverlay } from '@ardoq/loader';

export const REPORT_DATASOURCE_MIGRATION_ID = 'report-datasource-migration';

const LoadingAnimation = styled(LoadingOverlay)`
  position: relative;
`;
const List = styled.ul`
  padding: 0;
`;

type RowData = {
  name: string;
  createdBy: string | null;
  reason: string;
  invalidQueries: { queries: string[] };
  editReport: { reportId: string };
};

const InvalidQueriesCell = ({
  queries,
}: RowData['invalidQueries']): React.ReactNode => (
  <List>
    {queries.map((queryString, i) => (
      <li key={i}>{queryString}</li>
    ))}
  </List>
);

const getEditReportCell =
  (i18nLocale: I18nLocale) =>
  ({ reportId }: RowData['editReport']): React.ReactNode => (
    <TertiaryButton
      onClick={() =>
        openReportInBuilder(ReportEventLocations.ARDOQ, { reportId })
      }
    >
      {t(i18nLocale, 'migrationChecklist:reportTable.editButton', 'Edit')}
    </TertiaryButton>
  );

const getColumns = (i18nLocale: I18nLocale): Column<RowData>[] => [
  {
    title: t(
      i18nLocale,
      'migrationChecklist:reportTable.reportName',
      'Report name'
    ),
    id: 'name',
    width: 300,
  },
  {
    title: t(i18nLocale, 'migrationChecklist:reportTable.author', 'Author'),
    id: 'createdBy',
  },
  {
    title: t(
      i18nLocale,
      'migrationChecklist:reportTable.reason',
      'Reason for manual review'
    ),
    id: 'reason',
  },
  {
    title: t(
      i18nLocale,
      'migrationChecklist:reportTable.queryPart',
      'Query part'
    ),
    id: 'invalidQueries',
    cellRenderer: InvalidQueriesCell,
    valueGetter: ({ queries }) => queries.join(', '),
  },
  {
    title: t(
      i18nLocale,
      'migrationChecklist:reportTable.editReport',
      'Edit report'
    ),
    id: 'editReport',
    cellRenderer: getEditReportCell(i18nLocale),
    valueGetter: ({ reportId }) => reportId,
  },
];

const toRows = (data: ReportDatasourceMigrationData[]): Row<RowData>[] =>
  data.map(item => ({
    id: item._id,
    data: {
      name: item.name,
      createdBy: item.createdBy,
      reason: item.reason,
      invalidQueries: { queries: item.invalidQueries },
      editReport: { reportId: item._id },
    },
  }));

type ReportDatasourceChecklistContentProps = {
  data: ReportDatasourceMigrationData[] | null;
  i18nLocale: I18nLocale;
};
const ReportDatasourceChecklistContent = ({
  data,
  i18nLocale,
}: ReportDatasourceChecklistContentProps) => {
  const columns = useMemo(() => getColumns(i18nLocale), [i18nLocale]);
  return data === null ? (
    <LoadingAnimation
      message={t(i18nLocale, 'migrationChecklist:loading', 'Loading...')}
    />
  ) : data.length ? (
    <Table<RowData>
      rows={toRows(data)}
      columns={columns}
      sortable={{ descriptor: null }}
      height="100%"
      aria-label={t(
        i18nLocale,
        'migrationChecklist:reportTable.ariaLabel',
        'Report datasource migration checklist'
      )}
      emptyStateLabels={getTableEmptyStateLabels(i18nLocale)}
    />
  ) : (
    <Box>
      <Stack gap="medium">
        <FlexBox justify="center">
          <Header1>
            {t(i18nLocale, 'migrationChecklist:allDone.title', 'All done!')}
          </Header1>
        </FlexBox>
        <FlexBox justify="center">
          <Paragraph>
            {t(
              i18nLocale,
              'migrationChecklist:allDone.reportsMessage',
              'There are no more reports to be migrated. Nice work!'
            )}
          </Paragraph>
        </FlexBox>
      </Stack>
    </Box>
  );
};

export { ReportDatasourceChecklistContent };
